import { defineComponent, inject, nextTick, onMounted, ref, Ref, watch, computed } from 'vue';
import CompanyService from '@/sections/admin/entities/company/company.service';
import { Company } from '@/shared/model/company.model';
import { useStore } from '@/plugins/vuex';

export default defineComponent({
  props: {
    defaultCompanyId: {
      type: String,
      required: false
    }
  },
  setup(props) {
    const store = useStore();
    const companyService = inject('companyService') as CompanyService;
    const companies: Ref<Company[]> = ref([]);
    const selectedItem = ref<Company>(store.getters['cartStore/companySelected']);
    const isDisabled = ref(false);
    const companyAutocomplete = ref(null);
    const labelDisplay = ref('');

    onMounted(async () => {
      await retrieveAllCompanies();
      if (props.defaultCompanyId) {
        setDefaultCompany(companies.value.find(company => company.id === props.defaultCompanyId));
        return;
      }
    });

    watch(
      () => props.defaultCompanyId,
      newValue => {
        setDefaultCompany(companies.value.find(company => company.id === props.defaultCompanyId));
      }
    );

    const setDefaultCompany = (company: Company) => {
      isDisabled.value = true;
      selectedItem.value = company;
      if (company) store.commit('cartStore/setCompany', company);
    };

    const retrieveAllCompanies = async () => {
      try {
        const response = await companyService.retrieve();
        const data = Array.isArray(response) ? response : [];
        const processedCompanies = data.map(company => ({
          ...company
        }));
        companies.value = sortCompanies(processedCompanies);
      } catch (error) {
        console.error('Error retrieving companies:', error);
      }
    };

    const sortCompanies = (companies: Company[]) => {
      return companies.sort((a, b) => {
        const companyA = a.name?.toLowerCase() || '';
        const companyB = b.name?.toLowerCase() || '';
        if (!companyA) return 1;
        if (!companyB) return -1;
        return companyA.localeCompare(companyB);
      });
    };

    const groupedCompanies = computed(() => companies.value);

    const onCompanySelected = (company: Company) => {
      store.commit('cartStore/setCompany', company);
    };

    const scrollToStart = () => {
      const input = companyAutocomplete.value.$el.querySelector('input');
      if (input) {
        input.scrollLeft = 0;
      }
    };

    const isItemSelected = item => {
      setLabelDisplay();
      if (!selectedItem.value) return false;
      return selectedItem.value.id === item.id;
    };

    const setLabelDisplay = () => {
      const itemDesc = selectedItem.value?.name;
      labelDisplay.value = `${itemDesc}`;
    };

    const formatItemName = (item: Company) => {
      return item.name || '';
    };

    const formatItemText = (item: Company) => {
      const city = item.city ? `, ${item.city}` : '';
      return `${formatItemName(item)}${city}`;
    };

    return {
      companyAutocomplete,
      selectedItem,
      companies,
      groupedCompanies,
      isDisabled,
      onCompanySelected,
      scrollToStart,
      isItemSelected,
      formatItemText,
      formatItemName
    };
  }
});
